/* Keep Judge.me's media gallery keyboard-accessible without replacing its controls. */
if (!customElements.get('lala-review-gallery')) {
  const galleryLabels = {
    de: {
      gallery: 'Bewertungsgalerie', close: 'Schliessen', previous: 'Vorheriges Medium', next: 'Nächstes Medium',
      thumbnails: 'Medienauswahl', item: 'Medium', photo: 'Bewertungsbild',
      review: (author, rating, title) => `Bewertung von ${author} öffnen: ${rating} ${rating === 1 ? 'Stern' : 'Sterne'}. ${title}`,
      rating: value => `${value} von 5 Sternen`
    },
    en: {
      gallery: 'Review gallery', close: 'Close', previous: 'Previous item', next: 'Next item',
      thumbnails: 'Media selection', item: 'Item', photo: 'Review Image',
      review: (author, rating, title) => `Open review by ${author}: ${rating} ${rating === 1 ? 'star' : 'stars'}. ${title}`,
      rating: value => `${value} out of 5 stars`
    },
    fr: {
      gallery: 'Galerie des avis', close: 'Fermer', previous: 'Précédent', next: 'Suivant',
      thumbnails: 'Sélection des médias', item: 'Média', photo: 'Photo de l’avis',
      review: (author, rating, title) => `Ouvrir cet avis : ${author} (${rating} ${rating > 1 ? 'étoiles' : 'étoile'}). ${title}`,
      rating: value => `${value} ${value > 1 ? 'étoiles' : 'étoile'} sur 5`
    },
    it: {
      gallery: 'Galleria delle recensioni', close: 'Chiudi', previous: 'Precedente', next: 'Successivo',
      thumbnails: 'Selezione dei contenuti', item: 'Contenuto', photo: 'Immagine della recensione',
      review: (author, rating, title) => `Apri la recensione di ${author}: ${rating} ${rating === 1 ? 'stella' : 'stelle'}. ${title}`,
      rating: value => `${value} ${value === 1 ? 'stella' : 'stelle'} su 5`
    }
  };

  const reviewPattern = /^Review by (.+): ([0-5]) (?:star|stars)\. (.+)$/;
  const ratingPattern = /^([0-5]) out of 5 stars$/;

  class LalaReviewGallery extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.labels = galleryLabels[this.lang.split('-')[0]] || galleryLabels.en;
      this.intentId = 0;
      this.staleDialogs = new Set();
      this.leavingDialogs = new Set();
      this.onIntent = this.onIntent.bind(this);
      this.onEscape = this.onEscape.bind(this);
      this.onHostMutations = this.onHostMutations.bind(this);
      this.onDocumentMutations = this.onDocumentMutations.bind(this);
      this.onDialogMutations = this.onDialogMutations.bind(this);
      this.hostObserver = new MutationObserver(this.onHostMutations);
      this.documentObserver = new MutationObserver(this.onDocumentMutations);
      this.dialogObserver = new MutationObserver(this.onDialogMutations);
      this.addEventListener('click', this.onIntent, true);
      this.addEventListener('keydown', this.onIntent, true);
      this.hostObserver.observe(this, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['aria-label', 'alt', 'role', 'aria-haspopup', 'tabindex']
      });
      this.localizeHost();
    }

    disconnectedCallback() {
      this.intentId += 1;
      this.finish(false, this.intentId);
      this.hostObserver?.disconnect();
      this.removeEventListener('click', this.onIntent, true);
      this.removeEventListener('keydown', this.onIntent, true);
      this.initialized = false;
    }

    visible(element) {
      return Boolean(element?.isConnected && element.getClientRects().length &&
        element.getAttribute('aria-hidden') !== 'true' &&
        getComputedStyle(element).visibility !== 'hidden');
    }

    setLabel(element, label) {
      if (element && element.getAttribute('aria-label') !== label) {
        element.setAttribute('aria-label', label);
      }
    }

    localizeReviewCard(card) {
      if (card.getAttribute('role') !== 'button') card.setAttribute('role', 'button');
      if (card.getAttribute('aria-haspopup') !== 'dialog') card.setAttribute('aria-haspopup', 'dialog');
      if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '0');
      const match = card.getAttribute('aria-label')?.match(reviewPattern);
      if (!match) return;
      const rating = Number(match[2]);
      this.setLabel(card, this.labels.review(match[1], rating, match[3]));
    }

    localizeRating(rating) {
      const match = rating.getAttribute('aria-label')?.match(ratingPattern);
      if (!match) return;
      this.setLabel(rating, this.labels.rating(Number(match[1])));
    }

    localizePhoto(image) {
      if (image.getAttribute('alt') === 'Review Image' && image.getAttribute('alt') !== this.labels.photo) {
        image.setAttribute('alt', this.labels.photo);
      }
    }

    localizeWithin(root) {
      root.querySelectorAll('.jdgm-review-card').forEach(card => this.localizeReviewCard(card));
      root.querySelectorAll('.jm-star-rating[role="img"]').forEach(rating => this.localizeRating(rating));
      root.querySelectorAll('img[alt="Review Image"]').forEach(image => this.localizePhoto(image));
    }

    localizeHost() {
      this.localizeWithin(this);
    }

    onHostMutations() {
      if (this.isConnected) this.localizeHost();
    }

    onIntent(event) {
      if (event.type === 'keydown' && !['Enter', ' '].includes(event.key)) return;
      if (!(event.target instanceof Element)) return;
      const card = event.target.closest('.jdgm-review-card');
      if (!card || !this.contains(card)) return;
      // Judge.me opens on Enter keydown. Cancel only the browser's later implicit click,
      // which would otherwise follow focus into the newly opened dialog and close it.
      if (event.type === 'keydown' && event.key === 'Enter') event.preventDefault();

      this.intentId += 1;
      const intentId = this.intentId;
      cancelAnimationFrame(this.focusFrame);
      cancelAnimationFrame(this.restoreFrame);
      cancelAnimationFrame(this.closeFrame);
      clearTimeout(this.openTimer);

      const visibleDialogs = this.findVisibleDialogs();
      this.staleDialogs = new Set(visibleDialogs);
      this.leavingDialogs = new Set(visibleDialogs.filter(dialog => this.leaving(dialog)));
      if (this.dialog) this.releaseDialog(this.dialog);
      this.opener = card;

      // The vendor portals its dialog outside the app section.
      this.documentObserver.disconnect();
      this.documentObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
      for (const dialog of this.staleDialogs) {
        this.dialogObserver.observe(dialog.parentElement || dialog, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-label', 'alt']
        });
      }
      this.openTimer = setTimeout(() => {
        if (intentId === this.intentId && !this.dialog) this.finish(false, intentId);
      }, 5000);
      this.focusFrame = requestAnimationFrame(() => {
        if (intentId === this.intentId) this.syncDialog();
      });
    }

    findVisibleDialogs() {
      return [...document.querySelectorAll('.jdgm-modal__content[role="dialog"][aria-modal="true"]')]
        .filter(candidate => this.visible(candidate) && candidate.querySelector('.jm-indicator__close'));
    }

    mutationTouchesDialog(mutation, dialog) {
      const target = mutation.target;
      return target === dialog || dialog.contains(target) ||
        (target instanceof Element && target.contains(dialog));
    }

    onDocumentMutations(mutations) {
      if (!this.isConnected) return;
      this.syncDialog();
    }

    onDialogMutations(mutations) {
      if (!this.isConnected) return;
      for (const dialog of this.staleDialogs) {
        if (mutations.some(mutation => this.mutationTouchesDialog(mutation, dialog)) && this.leaving(dialog)) {
          this.leavingDialogs.add(dialog);
        }
      }
      this.syncDialog();
    }

    leaving(dialog) {
      return [dialog, dialog?.parentElement].some(element =>
        ['jdgm-modal__fade-leave-from', 'jdgm-modal__fade-leave-active', 'jdgm-modal__fade-leave-to']
          .some(className => element?.classList.contains(className))
      );
    }

    findPendingDialog() {
      const dialogs = this.findVisibleDialogs();
      return dialogs.find(dialog => !this.staleDialogs.has(dialog)) ||
        dialogs.find(dialog => this.leavingDialogs.has(dialog) && !this.leaving(dialog)) || null;
    }

    syncDialog() {
      if (!this.isConnected) return;
      if (this.dialog) {
        if (this.visible(this.dialog)) {
          this.localizeDialog();
          return;
        }
        const closedDialog = this.dialog;
        const intentId = this.dialogIntentId;
        const replacement = this.findVisibleDialogs().find(dialog => dialog !== closedDialog);
        if (replacement) {
          this.releaseDialog(closedDialog);
          this.adoptDialog(replacement, intentId);
          return;
        }
        if (closedDialog.isConnected) return;
        this.releaseDialog(closedDialog);
        cancelAnimationFrame(this.closeFrame);
        this.closeFrame = requestAnimationFrame(() => {
          if (intentId === this.intentId && !this.dialog) this.finish(true, intentId, closedDialog);
        });
        return;
      }
      if (!this.opener) return;
      const dialog = this.findPendingDialog();
      if (dialog) this.adoptDialog(dialog, this.intentId);
    }

    adoptDialog(dialog, intentId) {
      if (intentId !== this.intentId || !this.visible(dialog)) return;
      if (this.dialog && this.dialog !== dialog) this.releaseDialog(this.dialog);
      this.dialog = dialog;
      this.dialogIntentId = intentId;
      this.staleDialogs.clear();
      this.leavingDialogs.clear();
      clearTimeout(this.openTimer);
      cancelAnimationFrame(this.closeFrame);
      dialog.setAttribute('data-lala-review-gallery', '');
      this.dialogObserver.disconnect();
      this.dialogObserver.observe(dialog.parentElement || dialog, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style', 'hidden', 'aria-hidden', 'aria-label', 'alt']
      });
      this.localizeDialog();
      document.removeEventListener('keydown', this.onEscape);
      document.addEventListener('keydown', this.onEscape);
      cancelAnimationFrame(this.focusFrame);
      this.focusFrame = requestAnimationFrame(() => {
        if (intentId === this.intentId && this.dialog === dialog &&
          this.visible(dialog) && !dialog.contains(document.activeElement)) {
          dialog.querySelector('.jm-indicator__close')?.focus({ preventScroll: true });
        }
      });
    }

    localizeDialog() {
      const dialog = this.dialog;
      if (!dialog) return;
      this.setLabel(dialog, this.labels.gallery);
      for (const [selector, label] of [
        ['.jm-indicator__close', this.labels.close],
        ['.jm-navigation__previous', this.labels.previous],
        ['.jm-navigation__next', this.labels.next],
        ['.jm-media-grid__container[role="group"]', this.labels.thumbnails]
      ]) {
        this.setLabel(dialog.querySelector(selector), label);
      }
      dialog.querySelectorAll('.jm-media-grid__item').forEach((button, index) => {
        this.setLabel(button, `${this.labels.item} ${index + 1}`);
      });
      const thumbnailHeading = dialog.querySelector('.jm-media-grid > .sr-only');
      if (thumbnailHeading && thumbnailHeading.textContent !== this.labels.thumbnails) {
        thumbnailHeading.textContent = this.labels.thumbnails;
      }
      this.localizeWithin(dialog);
    }

    onEscape(event) {
      if (event.key !== 'Escape' || event.defaultPrevented || event.isComposing || !this.visible(this.dialog)) return;
      const otherDialog = event.target instanceof Element && event.target.closest('[role="dialog"]');
      if (otherDialog && otherDialog !== this.dialog) return;
      const close = this.dialog.querySelector('.jm-indicator__close');
      if (!this.visible(close) || close.disabled) return;
      event.preventDefault();
      close.click();
    }

    releaseDialog(dialog) {
      if (!dialog || this.dialog !== dialog) return;
      dialog.removeAttribute('data-lala-review-gallery');
      this.dialogObserver.disconnect();
      document.removeEventListener('keydown', this.onEscape);
      this.dialog = null;
      this.dialogIntentId = null;
    }

    finish(restoreFocus, intentId = this.intentId, closedDialog = this.dialog) {
      if (intentId !== this.intentId) return;
      const opener = this.opener;
      if (this.dialog) this.releaseDialog(this.dialog);
      this.documentObserver?.disconnect();
      this.dialogObserver?.disconnect();
      document.removeEventListener('keydown', this.onEscape);
      clearTimeout(this.openTimer);
      cancelAnimationFrame(this.focusFrame);
      cancelAnimationFrame(this.restoreFrame);
      cancelAnimationFrame(this.closeFrame);
      this.dialog = null;
      this.dialogIntentId = null;
      this.opener = null;
      this.staleDialogs.clear();
      this.leavingDialogs.clear();
      if (!restoreFocus || !this.visible(opener)) return;
      this.restoreFrame = requestAnimationFrame(() => {
        const active = document.activeElement;
        // Respect focus deliberately moved to a different control during dismissal.
        if (intentId === this.intentId && !this.opener && !this.dialog && this.isConnected && this.visible(opener) &&
          (active === document.body || active === document.documentElement || closedDialog?.contains(active))) {
          opener.focus({ preventScroll: true });
        }
      });
    }
  }

  customElements.define('lala-review-gallery', LalaReviewGallery);
}
